import Controller from "controllers/application_controller"
import { PopoverPositioning } from "../lib/popover_positioning"

// The first tooltip waits for the hover delay, half Base UI's 600ms provider
// default; while one is open, and for the group timeout (Base UI's) after one
// closes, the next opens at once.
const HOVER_DELAY = 300
const GROUP_TIMEOUT = 400

/**
 * Tooltip behaviour for every trigger on the page (see tooltip.css for the
 * scope and DOM contract). Declared once on <html>. A trigger opts in as a
 * `trigger` target and is labelled by data-ui-tooltip-content or, failing
 * that, its aria-label; data-ui-tooltip-side places it and
 * data-ui-tooltip-disabled keeps it closed. One [popover="manual"] popup
 * serves every trigger: appended to <body> on first open, as Base UI's portal
 * mounts it, re-anchored to each trigger as it shows, and mounted afresh
 * after Turbo replaces the body.
 *
 * Opens on mouse hover after the delay and on keyboard focus at once; closes
 * on leave, blur, press or keyboard activation, and Escape. Escape is left to
 * bubble: closing a hint must not stop a surrounding dialog from closing too.
 * Touch never opens it, as upstream: there is no way to hover before tapping.
 */
export default class extends Controller {
  static identifier = "ui-tooltip"
  static targets = ["trigger"]

  popup = null
  positioning = null
  trigger = null
  pendingTrigger = null
  openTimer = null
  groupUntil = 0

  connect() {
    // Enter and leave don't bubble; a capturing listener on the root still
    // sees each element's own.
    this.addEventListener("pointerenter", event => this.pointerEntered(event), { capture: true })
    this.addEventListener("pointerleave", event => this.pointerLeft(event), { capture: true })
    this.addEventListener("pointerdown", () => this.hide())
    this.addEventListener("click", () => this.hide())
    this.addEventListener("change", event => this.changed(event))
    this.addEventListener("focusin", event => this.focused(event))
    this.addEventListener("focusout", event => this.blurred(event))
    this.addEventListener("keydown", event => this.keyPressed(event))
  }

  disconnect() {
    this.hide()
    this.positioning?.unpairAnchor()
    this.popup?.remove()
    this.popup = null
    super.disconnect()
  }

  // A Turbo frame replacing the open trigger takes the popup's anchor with
  // it, so the exit fade would play at the viewport's corner.
  triggerTargetDisconnected(trigger) {
    if (trigger === this.trigger) this.hide({ instant: true })
    if (trigger === this.pendingTrigger) this.cancelPendingOpen()
  }

  pointerEntered(event) {
    if (event.pointerType === "touch" || !this.isTrigger(event.target)) return

    if (this.groupActive) {
      this.show(event.target, "delay")
    } else {
      this.scheduleOpen(event.target)
    }
  }

  pointerLeft(event) {
    if (event.target === this.pendingTrigger) this.cancelPendingOpen()
    if (event.target === this.trigger) this.hide()
  }

  // Keyboard focus only: a click's focus does not match :focus-visible on
  // buttons and links, so pressing the trigger leaves the tooltip closed.
  // Focus within a trigger counts: the report chart's wrapper holds a button.
  focused(event) {
    const trigger = event.target.closest(this.triggerSelector)
    if (!trigger || !event.target.matches(":focus-visible")) return

    this.show(trigger, "focus")
  }

  blurred(event) {
    if (this.trigger?.contains(event.target)) this.hide()
  }

  // The entry form's owner select sits inside its trigger and sets the
  // content attribute on change; the open label follows.
  changed(event) {
    if (this.trigger?.contains(event.target)) this.popup.textContent = this.content(this.trigger)
  }

  keyPressed(event) {
    if (event.key === "Escape") this.hide()
  }

  scheduleOpen(trigger) {
    this.cancelPendingOpen()
    this.pendingTrigger = trigger
    this.openTimer = setTimeout(() => this.show(trigger), HOVER_DELAY)
  }

  cancelPendingOpen() {
    clearTimeout(this.openTimer)
    this.openTimer = null
    this.pendingTrigger = null
  }

  // instantType is Base UI's data-instant: the popup skips its transition on
  // keyboard focus and while moving between triggers within the group.
  show(trigger, instantType = null) {
    this.cancelPendingOpen()
    if (trigger === this.trigger || this.disabled(trigger)) return

    this.hide()
    // hide() keeps the anchor for the exit fade; release it now the popup moves on.
    this.positioning?.unpairAnchor()
    if (!this.popup?.isConnected) this.popup = this.createPopup()

    const side = trigger.dataset.uiTooltipSide ?? "top"
    this.popup.textContent = this.content(trigger)
    this.popup.dataset.side = side
    if (instantType) {
      this.popup.dataset.instant = instantType
    } else {
      delete this.popup.dataset.instant
    }
    this.positioning = new PopoverPositioning({
      reference: trigger,
      floating: this.popup,
      namePrefix: "ui-tooltip",
      placement: side
    })

    this.popup.showPopover()
    this.positioning.start()
    this.trigger = trigger
  }

  hide({ instant = false } = {}) {
    this.cancelPendingOpen()
    this.positioning?.stop()
    this.trigger = null
    if (!this.open) return

    if (instant) this.popup.dataset.instant = "dismiss"
    this.popup.hidePopover()
    this.groupUntil = Date.now() + GROUP_TIMEOUT
  }

  get open() {
    return this.popup?.matches(":popover-open") ?? false
  }

  get groupActive() {
    return this.open || Date.now() < this.groupUntil
  }

  get triggerSelector() {
    return `[data-${this.identifier}-target~="trigger"]`
  }

  isTrigger(element) {
    return element.matches(this.triggerSelector)
  }

  content(trigger) {
    return trigger.dataset.uiTooltipContent ?? trigger.getAttribute("aria-label")
  }

  disabled(trigger) {
    return trigger.hasAttribute("data-ui-tooltip-disabled")
  }

  createPopup() {
    const popup = document.createElement("div")
    popup.className = "tw ui-tooltip"
    popup.setAttribute("popover", "manual")
    popup.dataset.slot = "tooltip-content"
    // Turbo leaves it out of page snapshots: a restored page mounts a fresh one.
    popup.dataset.turboTemporary = ""
    document.body.append(popup)
    return popup
  }
}
